import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import axios from "axios"; // Import axios
import "../css/UserManagement.css";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShare, faKey } from "@fortawesome/free-solid-svg-icons";
import { faCloudDownload } from "@fortawesome/free-solid-svg-icons";

import {
  CheckBox,
  DatePicker,
  DropdownBox,
  InputBox,
  SubmitButton,
  UploadBox,
} from "../../components/Input";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import { showToast } from "../../components/toast";
import LoadingSpinner from '../../components/LoadingSpinner';
import { set } from "date-fns";


const isFieldEditable = (id, fieldName) => {
  if (!id) return true;
  const permissionKey = fieldName === "user_type" ? "user_role" : fieldName;
  return hasPermission(permissionKey, "edit");
};

const isFieldVisible = (fieldName) => {
  const permissionKey = fieldName === "user_type" ? "user_role" : fieldName;
  return hasPermission(permissionKey, "view");
};

// Memoized input component
const MemoizedInput = memo(
  ({
    fieldname,
    label,
    placeholder,
    value,
    onChange,
    disabled,
    readOnly,
    required,
    errorMessage,
  }) => {
    const inputRef = useRef(null);

    const handleFocus = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    return (
      <div className="form-group" onClick={handleFocus}>
        <label className="control-label">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
        <input
          ref={inputRef}
          type="text"
          name={fieldname}
          className={`form-control ${errorMessage ? "is-invalid" : ""}`}
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          autoComplete="off"
        />
        {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
      </div>
    );
  }
);

// Permission-aware input component
const PermissionInputBox = ({
  id,
  fieldname,
  label,
  placeholder,
  value,
  onChange,
  required = false,
  errorMessage,
}) => {
  if (!isFieldVisible(fieldname)) return null;
  const canEdit = isFieldEditable(id, fieldname);

  return (
    <MemoizedInput
      fieldname={fieldname}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={!canEdit}
      readOnly={!canEdit}
      required={required}
      errorMessage={errorMessage}
    />
  );
};

const AddUser = () => {
  const { id } = useParams(); // Get the ID from the URL
  const navigate = useNavigate(); // Initialize useNavigate

  // Use consistent field names that match permission keys
  const [formData, setFormData] = useState({
    user_name: "",
    user_password: "",
    user_fullname: "",
    user_personal_mobile: "",
    user_office_mobile: "",
    mobile_link_adhar: "",
    user_whatsapp: "",
    user_personal_emai: "",
    user_office_email: "",
    user_dob: "",
    sex: "",
    marital_status: "",
    father_name: "",
    user_image: "",
    user_uan_number: "",
    user_total_ctc: "",
    user_permonth_salary: "",
    user_incentives: "",
    user_incentives_type: "",
    user_monthly_target: "",
    incentive_per_subscriber: "",
    user_designation: "",
    user_type: "",
    user_joining_date: "",
    joining_interview_by: "",
    left_interview_by: "",
    left_reason: "",
    user_left_date: "",
    user_suspension_date: "",
    user_employmentstatus: "",
    user_employmenttype: "",
    permanent_address: "",
    user_address: "",
    user_pan: "",
    user_pan_file: "",
    user_aadhar: "",
    user_aadhar_file: "",
    your_name_in_bank: "",
    user_bank_name: "",
    user_bank_ifsc: "",
    user_bank_account: "",
    nominee_name: "",
    nominee_dob: "",
    relationship_with_nominee: "",
    botsense_user: "",
    botsense_password: "",
    user_set_active: false,
    user_edit_checkbox: false,
    user_languages: [],
  });

  const [errorMessages, setErrorMessages] = useState({}); // State for error messages
  const [loading, setLoading] = useState(false); // New state to handle loading
  const token = localStorage.getItem("authToken");
  const [userLogs, setUserLogs] = useState([]);

  const [dropdownOptions, setDropdownOptions] = useState({
    userroles: [],
    designationlist: [],
    employmentstatuslist: [],
    employmenttypelist: [],
    languages: [],
    user_category: [],
    gender: [],
    marital_status: [],
    insentives: [],
    relationship_nominee: [],
  });

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const response = await fetch(`${API_URL}user/get-dropdown`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setDropdownOptions({
          userroles: data.data?.userroles || [],
          designationlist: data.data?.designationlist || [],
          employmentstatuslist: data.data?.employmentstatuslist || [],
          employmenttypelist: data.data?.employmenttypelist || [],
          languages: data.data?.languages || [],
          user_category: data.data?.user_category || [],
          gender: data.data?.gender || [],
          marital_status: data.data?.marital_status || [],
          insentives: data.data?.insentives || [],
          relationship_nominee: data.data?.relationship_nominee || [],
        });
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };

    fetchDropdownOptions();
  }, [token]);

  useEffect(() => {
    // Fetch user data if an ID is present
    const fetchUserData = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await axios.get(`${API_URL}user/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const userData = response.data.data.user;
          const logsData = response.data.data.userLogs || [];
          console.log(userData);
          // Assuming the response contains the user data
          setFormData({
            user_name: userData.user_name,
            user_password: "", // Do not pre-fill password for security reasons
            user_fullname: userData.user_fullname,
            user_personal_mobile: userData.user_mob,
            user_office_mobile: userData.user_mob2,
            mobile_link_adhar: userData.mobile_link_adhar,
            user_whatsapp: userData.user_whatsapp,
            user_personal_emai: userData.user_email,
            user_office_email: userData.office_email,
            user_dob: userData.user_dob,
            sex: userData.sex,
            marital_status: userData.marital_status,
            father_name: userData.father_name,
            user_image: userData.user_image,
            user_uan_number: userData.uan_number,
            user_total_ctc: userData.basic_pay,
            user_permonth_salary: userData.salary,
            user_incentives: userData.incentives,
            user_incentives_type: userData.incentives_type,
            user_monthly_target: userData.monthly_target,
            incentive_per_subscriber: userData.incentive_per_subscriber ? Math.floor(userData.incentive_per_subscriber) : "",
            user_joining_date: userData.join_date,
            joining_interview_by: userData.joining_interview_by,
            left_interview_by: userData.left_interview_by,
            left_reason: userData.left_reason,
            user_designation: userData.user_designation,
            user_left_date: userData.left_date,
            user_employmentstatus: userData.employmentstatus,
            user_employmenttype: userData.employmenttype,
            user_suspension_date: userData.suspension_date,
            permanent_address: userData.permanent_address,
            user_address: userData.user_address,
            user_pan: userData.user_pan,
            user_pan_file: userData.user_pan_image,
            user_aadhar: userData.user_aadhar,
            user_aadhar_file: userData.user_aadhar_image,
            your_name_in_bank: userData.name_in_bank,
            user_bank_name: userData.bank_name,
            user_bank_ifsc: userData.bank_ifsc,
            user_bank_account: userData.bank_account,
            nominee_name: userData.nominee_name,
            nominee_dob: userData.nominee_dob,
            relationship_with_nominee: userData.relationship_with_nominee,
            botsense_user: userData.botsense_user || "",
            botsense_password: userData.botsense_password || "",
            user_type: userData.user_type || userData.user_type || "",
            user_set_active: userData.user_blocked,
            user_edit_checkbox: userData.iscompleted,
            user_languages: userData.languages || [], // Set the languages if available
            // user_category: userData.user_category || "",
            user_category: userData.user_category ? Number(userData.user_category) : null
          });
          setUserLogs(logsData);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [id, token]);

  // Memoized handleChange function
  const handleChange = useCallback(
    (e) => {
      const { name, value, type, checked, files } = e.target;

      if (id && !isFieldEditable(id, name)) {
        console.log(`No edit permission for ${name}`);
        return;
      }

      let updatedValue;
      if (type === "file") {
        const file = files[0];
        if (file) {
          updatedValue = file;
          // Store filename for display
          setFormData((prev) => ({
            ...prev,
            [name + "_filename"]: file.name,
          }));
        } else {
          updatedValue = null;
          setFormData((prev) => ({
            ...prev,
            [name + "_filename"]: "",
          }));
        }
      } else if (type === "checkbox") {
        updatedValue = checked;
      } else {
        updatedValue = value;
        // Sanitize numeric fields to only allow integers if requested
        if (name === "incentive_per_subscriber") {
          updatedValue = value.replace(/\D/g, "");
        }
      }

      setFormData((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));

      if (errorMessages[name]) {
        setErrorMessages((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [id, errorMessages]
  );

  const handleGeneratePassword = useCallback(() => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const specialChars = "!@#$";
    const allChars = letters + numbers + specialChars;

    let password = "";
    password += letters.charAt(Math.floor(Math.random() * letters.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));

    for (let i = 0; i < 9; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    setFormData((prev) => ({
      ...prev,
      user_password: password,
    }));

    if (errorMessages.user_password) {
      setErrorMessages((prev) => ({
        ...prev,
        user_password: "",
      }));
    }
  }, [errorMessages]);

  // Define PermissionInputBox component

  const PermissionInputBox = useCallback(
    ({
      fieldname,
      label,
      placeholder,
      value,
      onChange,
      required = false,
      errorMessage,
    }) => {
      if (!isFieldVisible(fieldname)) return null;

      const canEdit = isFieldEditable(id, fieldname);

      return (
        <MemoizedInput
          fieldname={fieldname}
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={!canEdit}
          readOnly={!canEdit}
          required={required}
          errorMessage={errorMessage}
        />
      );
    },
    [id, isFieldVisible, isFieldEditable]
  );

  // Permission-aware DropdownBox component
  const PermissionDropdownBox = ({
    fieldname,
    label,
    options,
    value,
    onChange,
    errorMessage,
  }) => {
    if (!isFieldVisible(fieldname)) return null;

    const canEdit = isFieldEditable(id, fieldname);

    return (
      <div className="form-group">
        {label && <label className="control-label">{label}</label>}
        <select
          name={fieldname}
          className={`form-control ${errorMessage ? "is-invalid" : ""}`}
          value={value || ""}
          onChange={onChange}
          disabled={!canEdit}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
      </div>
    );
  };

  // Permission-aware DatePicker component
  const PermissionDatePicker = ({
    fieldname,
    label,
    value,
    onChange,
    errorMessage,
  }) => {
    if (!isFieldVisible(fieldname)) return null;

    const canEdit = isFieldEditable(id, fieldname);

    return (
      <div className="form-group">
        {label && <label className="control-label">{label}</label>}
        <input
          type="date"
          name={fieldname}
          className={`form-control ${errorMessage ? "is-invalid" : ""}`}
          value={value || ""}
          onChange={onChange}
          disabled={!canEdit}
          readOnly={!canEdit}
        />
        {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
      </div>
    );
  };

  // Permission-aware CheckBox component
  const PermissionCheckBox = ({ fieldname, id, ...props }) => {
    // Hide if not visible OR if has view-only permission
    if (!isFieldVisible(fieldname) || (id && !isFieldEditable(id, fieldname))) {
      return null;
    }

    return <CheckBox fieldname={fieldname} {...props} />;
  };

  // Permission-aware UploadBox component
  const PermissionUploadBox = ({
    fieldname,
    id,
    label,
    onChange,
    value,
    errorMessage,
  }) => {
    // Hide if field is not visible OR not editable (if ID exists)
    if (!isFieldVisible(fieldname) || (id && !isFieldEditable(id, fieldname))) {
      return null;
    }

    const canEdit = isFieldEditable(id, fieldname);
    const isImage = fieldname === "user_image";
    const fileName = formData[fieldname + "_filename"];

    return (
      <div className="form-group">
        <div className="row">
          <div className="col-md-12">
            {label && <label className="control-label">{label}</label>}
            <input
              type="file"
              name={fieldname}
              className={`form-control ${errorMessage ? "is-invalid" : ""}`}
              onChange={onChange}
              disabled={!canEdit}
              accept={isImage ? "image/*" : ".pdf,.jpg,.jpeg,.png"}
            />
          </div>
          <div className="col-md-12">
            {(fileName || value) && (
              <div className="value">
                <b>{fileName || (value && value.split("/").pop())}</b>
                {value && (
                  <a
                    href={value}
                    className="btn btn-default btn-xs float-right"
                    download
                  >
                    <i className="fa fa-download"></i>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
      </div>
    );
  };

  // Modified language handlers with permission checks
  const handleLanguageChange = (index, value) => {
    if (id && !isFieldEditable(id, "user_languages")) return;
    const newLanguageList = [...formData.user_languages];
    newLanguageList[index] = value;
    setFormData({ ...formData, user_languages: newLanguageList });
  };

  const addLanguage = () => {
    if (id && !isFieldEditable(id, "user_languages")) return;
    setFormData({
      ...formData,
      user_languages: [...formData.user_languages, ""],
    });
  };

  const removeLanguage = (index) => {
    if (id && !isFieldEditable(id, "user_languages")) return;
    const newLanguageList = formData.user_languages.filter(
      (_, i) => i !== index
    );
    setFormData({ ...formData, user_languages: newLanguageList });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const phoneRegex = /^\+?\d+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      formData.user_personal_mobile &&
      !phoneRegex.test(formData.user_personal_mobile)
    ) {
      showToast.error("Personal Mobile must contain only numbers.");
      setLoading(false);
      return;
    }
    if (
      formData.user_office_mobile &&
      !phoneRegex.test(formData.user_office_mobile)
    ) {
      showToast.error("Office Mobile must contain only numbers.");
      setLoading(false);
      return;
    }
    if (
      formData.mobile_link_adhar &&
      !phoneRegex.test(formData.mobile_link_adhar)
    ) {
      showToast.error("Mobile Link Adhar must contain only numbers.");
      setLoading(false);
      return;
    }
    if (formData.user_whatsapp && !phoneRegex.test(formData.user_whatsapp)) {
      showToast.error("WhatsApp must contain only numbers.");
      setLoading(false);
      return;
    }
    if (
      formData.user_personal_emai &&
      !emailRegex.test(formData.user_personal_emai)
    ) {
      showToast.error("Personal Email must be a valid email address.");
      setLoading(false);
      return;
    }
    if (
      formData.user_office_email &&
      !emailRegex.test(formData.user_office_email)
    ) {
      showToast.error("Office Email must be a valid email address.");
      setLoading(false);
      return;
    }
    // --- Password strength validation ---
    const isValidPassword = (password) => {
      const strongPasswordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/;
      return strongPasswordRegex.test(password);
    };

    if (formData.user_password && !isValidPassword(formData.user_password)) {
      showToast.error(
        "Password is not strong. It must contain at least 10 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character."
      );
      setLoading(false);
      return;
    }
    if (formData.user_dob) {
      const dobDate = new Date(formData.user_dob);
      const today = new Date();

      dobDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (dobDate > today) {
        showToast.error("Date of Birth cannot be greater than today.");
        setLoading(false);
        return;
      }
    }
    if (
      formData.user_permonth_salary &&
      !phoneRegex.test(formData.user_permonth_salary)
    ) {
      showToast.error("Per Month Salary must contain only numbers.");
      setLoading(false);
      return;
    }
    if (
      formData.user_incentives &&
      !phoneRegex.test(formData.user_incentives)
    ) {
      showToast.error("Incentives must contain only numbers.");
      setLoading(false);
      return;
    }
    if (
      formData.user_monthly_target &&
      !phoneRegex.test(formData.user_monthly_target)
    ) {
      showToast.error("Monthly Target must contain only numbers.");
      setLoading(false);
      return;
    }
    if (
      formData.incentive_per_subscriber &&
      !phoneRegex.test(formData.incentive_per_subscriber)
    ) {
      showToast.error("Incentive Per Subscriber must contain only numbers.");
      setLoading(false);
      return;
    }
    if (formData.nominee_dob) {
      const dobDate = new Date(formData.nominee_dob);
      const today = new Date();

      dobDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (dobDate > today) {
        showToast.error("Nominee Date of Birth cannot be greater than today.");
        setLoading(false);
        return;
      }
    }

    // Create FormData object
    const formDataToSend = new FormData();

    // Map frontend field names to backend field names
    const fieldMapping = {
      user_name: "user_name",
      user_password: "user_password",
      user_fullname: "user_fullname",
      user_personal_mobile: "user_mob",
      user_office_mobile: "user_mob2",
      mobile_link_adhar: "mobile_link_adhar",
      user_whatsapp: "user_whatsapp",
      user_personal_emai: "user_email",
      user_office_email: "office_email",
      user_dob: "user_dob",
      sex: "sex",
      marital_status: "marital_status",
      father_name: "father_name",
      user_image: "user_image",
      user_uan_number: "uan_number",
      user_total_ctc: "basic_pay",
      user_permonth_salary: "salary",
      user_incentives: "incentives",
      user_incentives_type: "incentives_type",
      user_monthly_target: "monthly_target",
      incentive_per_subscriber: "incentive_per_subscriber",
      user_joining_date: "join_date",
      joining_interview_by: "joining_interview_by",
      left_interview_by: "left_interview_by",
      left_reason: "left_reason",
      user_left_date: "left_date",
      user_suspension_date: "suspension_date",
      user_designation: "user_designation",
      user_employmentstatus: "employmentstatus",
      user_employmenttype: "employmenttype",
      permanent_address: "permanent_address",
      user_address: "user_address",
      user_pan: "user_pan",
      user_pan_file: "user_pan_image",
      user_aadhar: "user_aadhar",
      user_aadhar_file: "user_aadhar_image",
      your_name_in_bank: "your_name_in_bank",
      user_bank_name: "bank_name",
      user_bank_ifsc: "bank_ifsc",
      user_bank_account: "bank_account",
      nominee_name: "nominee_name",
      nominee_dob: "nominee_dob",
      relationship_with_nominee: "relationship_with_nominee",
      botsense_user: "botsense_user",
      botsense_password: "botsense_password",
      // user_role: "user_role",
      // Also send user_type to satisfy backend expecting this key
      user_type: "user_type",
      user_category: "user_category",
      user_set_active: "activate",
      user_edit_checkbox: "iscompleted",
      user_languages: "language",
    };


    const toYYYYMMDD = (val) => {
      if (!val || typeof val !== "string") return val;

      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

      // If in DD-MM-YYYY format, convert to YYYY-MM-DD
      if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
        const [d, m, y] = val.split("-");
        return `${y}-${m}-${d}`;
      }

      return val;
    };

    // Append all fields to FormData
    Object.entries(fieldMapping).forEach(([frontendField, backendField]) => {
      let value = formData[frontendField];

      // For user_type, mirror user_role value
      if (frontendField === "user_type" && value === undefined) {
        value = formData.user_type;
      }

      // Date fields formatting to DD-MM-YYYY as per API examples
      const dateBackends = [
        "user_dob",
        "join_date",
        "nominee_dob",
        "left_date",
        "suspension_date",
      ];
      if (dateBackends.includes(backendField)) {
        value = toYYYYMMDD(value);
      }

      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Handle arrays (like languages)
          if (backendField === "language") {
            // For language field, send as JSON array
            const validLanguages = value
              .filter(item => item !== "" && item !== null && item !== undefined)
              .map(item => {
                if (typeof item === "string") {
                  const numValue = parseInt(item, 10);
                  return !isNaN(numValue) ? numValue : null;
                }
                return typeof item === "number" ? item : null;
              })
              .filter(item => item !== null);

            // Only send language field if there are valid languages
            if (validLanguages.length > 0) {
              formDataToSend.append(backendField, JSON.stringify(validLanguages));
            }
          } else {
            // Handle other arrays
            value.forEach((item) => {
              if (item !== "") {
                formDataToSend.append(backendField, item);
              }
            });
          }
        } else if (typeof value === "boolean") {
          // Convert booleans to strings, specifically "0" or "1" for activate field
          if (backendField === "activate") {
            formDataToSend.append(backendField, value ? "1" : "0");
          } else {
            formDataToSend.append(backendField, value.toString());
          }
        } else if (value instanceof File) {
          // Handle file uploads
          formDataToSend.append(backendField, value, value.name);
        } else if (typeof value === "string" && !value.startsWith("data:")) {
          // Handle regular values, excluding data URLs
          formDataToSend.append(backendField, value);
        }
      }
    });

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      let response;
      if (id) {
        response = await axios.put(
          `${API_URL}user/update-user/${id}`,
          formDataToSend,
          config
        );
        toast.success("User updated successfully!");
      } else {
        response = await axios.post(
          `${API_URL}user/save-user`,
          formDataToSend,
          config
        );
        toast.success("User saved successfully!");
      }

      navigate("/Hrms/User", {
        state: { successMessage: "Operation successful!" },
      });
    } catch (error) {
      console.error("Error saving user:", error);
      let errorMessage = "An error occurred while saving the user.";

      if (error.response) {
        if (error.response.status === 400) {
          errorMessage =
            error.response.data.message ||
            "Bad request. Please check your input.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (type) => {
    try {
      if (!id) {
        toast.error("No user ID available for download");
        return;
      }

      const response = await axios.get(
        `${API_URL}user/${id}/download?type=${type}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob", // Important for file downloads
        }
      );

      // Create download link for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Set appropriate filename based on type
      let filename = "";
      switch (type) {
        case "aadhar":
          filename = `aadhar_${id}.png`;
          break;
        case "pan":
          filename = `pan_${id}.png`;
          break;
        case "user":
          filename = `user_${id}.png`;
          break;
        default:
          filename = `download_${id}.png`;
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`Download started for ${type} document`);
    } catch (error) {
      console.error(`Error downloading ${type}:`, error);
      let errorMessage = `Failed to download ${type} document`;

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = `${type} document not found for this user`;
        } else if (error.response.status === 403) {
          errorMessage = `You don't have permission to download ${type} documents`;
        }
      }

      toast.error(errorMessage);
    }
  };

  // Specific handlers for each download type
  const handleAadharDownload = async () => {
    setLoading(true);
    try {
      await downloadFile("aadhar");
    } finally {
      setLoading(false);
    }
  };
  const handlePanDownload = async () => {
    setLoading(true);
    try {
      await downloadFile('pan'); // Your existing downloadFile(type) function
    } finally {
      setLoading(false);
    }
  };
  const handleUserDownload = async () => {
    setLoading(true);
    try {
      await downloadFile("image");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {loading && <LoadingSpinner />}
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 mb-1">
            <span className="panel-title">Add User</span>
            <button
              onClick={handleUserDownload}
              className="btn btn-default btn-sm pull-right mx-1"
              disabled={!id || loading}
            >
              <FontAwesomeIcon icon={faDownload} /> User
            </button>
            <button
              onClick={handleAadharDownload}
              className="btn btn-default btn-sm pull-right mx-1"
              disabled={!id || loading}
            >
              <FontAwesomeIcon icon={faDownload} /> Aadhar
            </button>
            <button
              onClick={handlePanDownload}
              className="btn btn-default btn-sm pull-right mx-1"
            // disabled={!id || loading}
            >
              <FontAwesomeIcon icon={faDownload} /> Pancard
            </button>
            {/* <Link href="" className="btn btn-default btn-sm pull-right mx-1">
              <FontAwesomeIcon icon={faShare} /> Offer Letter
            </Link>
            <Link href="" className="btn btn-default btn-sm pull-right mx-1">
              <FontAwesomeIcon icon={faShare} /> Appointment Letter
            </Link> */}
          </div>
          <hr />
          <div className="panel-heading border-0">
            <span className="panel-title">Emp Personal Details</span>
          </div>
          <div className="panel-body">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="row">
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="User Name"
                    fieldname="user_name"
                    placeholder="Enter User Name"
                    value={formData.user_name}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_name}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="User Full Name"
                    fieldname="user_fullname"
                    placeholder="Enter User Full Name"
                    value={formData.user_fullname}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_fullname}
                    required
                  />
                </div>
                {/* <div className="col-md-4 mb-2">
                  <label>User Password </label>
                  <small>
                    {" "}
                    &nbsp;
                    <Link className="gen_pass" href="javascript:void(0)">
                      Generate Password
                    </Link>
                  </small>
                  <input
                    type="text"
                    name="userPassword"
                    placeholder="Enter User Password"
                    value={formData.user_password}
                    onChange={handleChange}
                    className="form-control"
                  />
                  {errorMessages.user_password && (
                    <div className="text-danger">
                      {errorMessages.user_password}
                    </div>
                  )}{" "}
                </div> */}
                <div className="col-md-4 mb-2">
                  {isFieldVisible("user_password") && (
                    <div className="form-group">
                      <label className="control-label">User Password</label>
                      <div className="input-group">
                        <input
                          type="text"
                          name="user_password"
                          placeholder="Enter User Password"
                          value={formData.user_password}
                          onChange={handleChange}
                          className={`form-control ${
                            errorMessages.user_password ? "is-invalid" : ""
                          }`}
                          autoComplete="new-password"
                          disabled={!isFieldEditable(id, "user_password")}
                          readOnly={!isFieldEditable(id, "user_password")}
                        />
                        {isFieldEditable(id, "user_password") && (
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: "500",
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                            }}
                            onClick={handleGeneratePassword}
                            title="Generate Random Password"
                          >
                            <FontAwesomeIcon icon={faKey} /> Generate
                          </button>
                        )}
                      </div>
                      {errorMessages.user_password && (
                        <div className="text-danger small mt-1">
                          {errorMessages.user_password}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Personal Mobile"
                    fieldname="user_personal_mobile"
                    placeholder="Enter Personal Mobile No."
                    value={formData.user_personal_mobile}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_personal_mobile} // Show error message
                    required
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Office Mobile"
                    fieldname="user_office_mobile"
                    placeholder="Enter Mobile No. 2"
                    value={formData.user_office_mobile}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Mobile No Linked with Aadhaar"
                    fieldname="mobile_link_adhar"
                    placeholder="Enter Mobile No. Linked with Aadhar"
                    value={formData.mobile_link_adhar}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Whatsapp No."
                    fieldname="user_whatsapp"
                    placeholder="Enter Whatsapp No."
                    value={formData.user_whatsapp}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Personal Email Id"
                    fieldname="user_personal_emai"
                    placeholder="Enter Email ID"
                    value={formData.user_personal_emai}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_personal_emai} // Show error message
                    required
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Office Email Id"
                    fieldname="user_office_email"
                    placeholder="Enter Email ID"
                    value={formData.user_office_email}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDatePicker
                    label="User  DOB"
                    fieldname="user_dob"
                    value={formData.user_dob}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="Sex"
                    fieldname="sex"
                    options={dropdownOptions.gender.map((g) => ({
                      label: g.name,
                      value: g._id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.sex}
                    errorMessage={errorMessages.sex} // Show error message
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="Marital Status"
                    fieldname="marital_status"
                    options={dropdownOptions.marital_status.map((m) => ({
                      label: m.name,
                      value: m._id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.marital_status}
                    errorMessage={errorMessages.marital_status} // Show error message
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Father Name"
                    fieldname="father_name"
                    placeholder="Enter Father Name"
                    value={formData.father_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="UAN Number"
                    fieldname="user_uan_number"
                    placeholder="Enter UAN Number"
                    value={formData.user_uan_number}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionUploadBox
                    label="User Image"
                    fieldname="user_image"
                    value={formData.user_image}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_image}
                    id={id}
                  />
                </div>
              </div>
              <hr />
              <div className="my-2">
                <span className="panel-title">Personal Details</span>
              </div>
              <div className="row">
                <div className="col-md-3 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Total CTC"
                    fieldname="user_total_ctc"
                    placeholder="Enter Total CTC"
                    value={formData.user_total_ctc}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Per Month Salary"
                    fieldname="user_permonth_salary"
                    placeholder="Enter Per Month Salary"
                    value={formData.user_permonth_salary}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <PermissionDropdownBox
                        label="Incentives Type"
                        fieldname="user_incentives_type"
                        options={dropdownOptions.insentives.map((i) => ({
                          label: i.name,
                          value: i._id,
                        }))} // Map to required format
                        onChange={handleChange}
                        value={formData.user_incentives_type}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <PermissionInputBox
                        id={id}
                        label="Incentives"
                        fieldname="user_incentives"
                        placeholder="Enter Incentives"
                        value={formData.user_incentives}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-3 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Incentive Per Subscriber"
                    fieldname="incentive_per_subscriber"
                    placeholder="Enter Incentive Per Subscriber"
                    value={formData.incentive_per_subscriber}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Monthly Target"
                    fieldname="user_monthly_target"
                    placeholder="Enter Monthly Target"
                    value={formData.user_monthly_target}
                    onChange={handleChange}
                  />
                </div>


                <div className="col-md-4 mb-2">
                  <PermissionDatePicker
                    label="Joining Date"
                    fieldname="user_joining_date"
                    value={formData.user_joining_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Joining Interview by"
                    fieldname="joining_interview_by"
                    placeholder="Enter Joining Interview by"
                    value={formData.joining_interview_by}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDatePicker
                    label="Left Date"
                    fieldname="user_left_date"
                    value={formData.user_left_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Left Interview by"
                    fieldname="left_interview_by"
                    placeholder="Enter Left Interview by"
                    value={formData.left_interview_by}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Left Reason"
                    fieldname="left_reason"
                    placeholder="Enter Left Reason"
                    value={formData.left_reason}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <hr />
              <div className="my-2">
                <span className="panel-title">Employment Details</span>
              </div>
              <div className="row">
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="Employment Status"
                    fieldname="user_employmentstatus"
                    options={dropdownOptions.employmentstatuslist.map((e) => ({
                      label: e.pick_name,
                      value: e._id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.user_employmentstatus}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="User  Designation"
                    fieldname="user_designation"
                    options={dropdownOptions.designationlist.map((d) => ({
                      label: d.pick_name,
                      value: d._id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.user_designation}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="Employment Type"
                    fieldname="user_employmenttype"
                    options={dropdownOptions.employmenttypelist.map((e) => ({
                      label: e.pick_name,
                      value: e._id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.user_employmenttype}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="User Role"
                    fieldname="user_type"
                    options={dropdownOptions.userroles.map((r) => ({
                      label: r.user_role,
                      value: r.id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.user_type}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="User  Category"
                    fieldname="user_category"
                    options={dropdownOptions.user_category.map((c) => ({
                      label: c.name,
                      value: c.id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.user_category}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDatePicker
                    label="Suspension Date"
                    fieldname="user_suspension_date"
                    value={formData.user_suspension_date}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <hr />
              <div className="my-2">
                <span className="panel-title">Communication Details</span>
              </div>
              <div className="row">
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Permanent address as per Aadhaar"
                    fieldname="permanent_address"
                    placeholder="Enter Permanent address as per Aadhaar"
                    value={formData.permanent_address}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="User  Address"
                    fieldname="user_address"
                    placeholder="Enter User Address"
                    value={formData.user_address}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="User  Pan"
                    fieldname="user_pan"
                    placeholder="Enter User Pan"
                    value={formData.user_pan}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionUploadBox
                    label="Pan File"
                    fieldname="user_pan_file"
                    value={formData.user_pan_file}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_pan_file}
                    id={id}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="User  Aadhar"
                    fieldname="user_aadhar"
                    placeholder="Enter User Aadhar"
                    value={formData.user_aadhar}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionUploadBox
                    id={id}
                    label="Aadhar File"
                    fieldname="user_aadhar_file"
                    value={formData.user_aadhar_file}
                    onChange={handleChange}
                    errorMessage={errorMessages.user_aadhar_file}
                  />
                </div>
              </div>
              <hr />
              <div className="my-2">
                <span className="panel-title">Bank Details</span>
              </div>
              <div className="row">
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Your name in Bank"
                    fieldname="your_name_in_bank"
                    placeholder="Enter Your name in Bank"
                    value={formData.your_name_in_bank}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Bank Name"
                    fieldname="user_bank_name"
                    placeholder="Enter Bank Name"
                    value={formData.user_bank_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Bank IFSC"
                    fieldname="user_bank_ifsc"
                    placeholder="Enter Bank IFSC"
                    value={formData.user_bank_ifsc}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Bank Account"
                    fieldname="user_bank_account"
                    placeholder="Enter Bank Account"
                    value={formData.user_bank_account}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <hr />
              <div className="my-2">
                <span className="panel-title">Nominee Details</span>
              </div>
              <div className="row">
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Nominee name as per Aadhaar"
                    fieldname="nominee_name"
                    placeholder="Enter Nominee name as per Aadhaar"
                    value={formData.nominee_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDatePicker
                    label="Nominee DOB"
                    fieldname="nominee_dob"
                    value={formData.nominee_dob}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionDropdownBox
                    label="Relationship with Nominee"
                    fieldname="relationship_with_nominee"
                    options={dropdownOptions.relationship_nominee.map((r) => ({
                      label: r.name,
                      value: r._id,
                    }))} // Map to required format
                    onChange={handleChange}
                    value={formData.relationship_with_nominee}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Botsense User"
                    fieldname="botsense_user"
                    placeholder="Enter Botsense User"
                    value={formData.botsense_user}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4 mb-2">
                  <PermissionInputBox
                    id={id}
                    label="Botsense Password"
                    fieldname="botsense_password"
                    placeholder="Enter Botsense Password"
                    value={formData.botsense_password}
                    onChange={handleChange}
                    type="password"      // To hide password input
                  />
                </div>
              </div>
              <hr />
              <div className="row">
                <div className="col-md-12 mb-2">
                  <PermissionCheckBox
                    label="Set Active"
                    fieldname="user_set_active"
                    checked={formData.user_set_active}
                    onChange={handleChange}
                    id={id} // Required for permission checks
                  />
                </div>
                <div className="col-md-12 mb-2">
                  <PermissionCheckBox
                    label="Uncheck To Allow User Edit"
                    fieldname="user_edit_checkbox"
                    checked={formData.user_edit_checkbox}
                    onChange={handleChange}
                    id={id} // Required for permission checks
                  />
                </div>
                <div className="row clonelist col-4 mt-2">
                  {isFieldVisible("user_languages") &&
                    isFieldEditable(id, "user_languages") && (
                      <div className="row clonelist col-12 mt-2">
                        <label className="control-label col-12">
                          Languages
                        </label>
                        <table className="table m-b-0 col-4">
                          <thead>
                            <tr>
                              <th style={{ textAlign: "center", width: "30%" }}>
                                <div className="th-inner">Name</div>
                              </th>
                              <th style={{ textAlign: "center", width: "10%" }}>
                                <div className="th-inner">Delete</div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.user_languages.map((value, index) => (
                              <tr key={index}>
                                <td>
                                  <select
                                    className="form-control"
                                    value={value}
                                    name="language[]"
                                    onChange={(e) =>
                                      handleLanguageChange(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    data-count={index}
                                    data-class="lang"
                                  >
                                    <option value="">Select Language</option>
                                    {dropdownOptions.languages.map(
                                      (language) => (
                                        <option
                                          key={language._id}
                                          value={language._id}
                                        >
                                          {language.name}
                                        </option>
                                      )
                                    )}
                                  </select>
                                </td>
                                <td
                                  style={{
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-gradient delete_row btn-alt pt5 pb5 mn"
                                    title="Delete Record"
                                    onClick={() => removeLanguage(index)}
                                  >
                                    {/* <i className="fa fa-cross"></i>X Delete */}
                                    X Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan="2" className="border-0">
                                <button
                                  type="button"
                                  className="addPick btn btn-success"
                                  onClick={addLanguage}
                                >
                                  Add Data
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                </div>
              </div>
              <SubmitButton pageBack="/Hrms/User" handleSubmit={handleSubmit} loading={loading} />
            </form>
          </div>
        </div>
        <div className="row mt-5">
          <div className="col-md-12">
            <div className="row">
              {formData.user_image && (
                <div className="col-md-4">
                  <img src={formData.user_image} style={{ width: "100%" }} alt="User" />
                </div>
              )}
              {formData.user_aadhar_file && (
                <div className="col-md-4">
                  <img src={formData.user_aadhar_file} style={{ width: "100%" }} alt="Aadhar" />
                </div>
              )}
              {formData.user_pan_file && (
                <div className="col-md-4">
                  <img src={formData.user_pan_file} style={{ width: "100%" }} alt="PAN" />
                </div>
              )}
              {formData.crm_profile_image && (
                <div className="col-md-4">
                  <img src={formData.crm_profile_image} style={{ width: "100%" }} alt="CRM Profile" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* User History like PHP view */}
        {userLogs && userLogs.length > 0 && (
          <div className="panel  panel-default with-rounded-corner with-shadow">
            <div className="panel-heading">
              <span className="panel-title">User History</span>
            </div>
            <div className="clearfix"></div>
            <div className="panel-body">
              <div className="row">
                <div style={{ overflowX: "auto", width: "100%" }}>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ maxWidth: "70% !important" }}>User History Details</th>
                        <th>Date Modified</th>
                      </tr>
                    </thead>
                    {/* <tbody>
                    {userLogs.map((log, idx) => {
                      let det = "";
                      if (log.colname === "join_date") det = "Join Date is ";
                      if (log.colname === "left_date") det = "Left Date is ";
                      if (log.colname === "suspension_date") det = "Suspension Date is ";
                      if (log.colname === "employmentstatus") det = "Employment Status is ";
                      const created = log.createdOn
                        ? new Date(log.createdOn).toLocaleString()
                        : "";
                      return (
                        <tr key={idx}>
                          <td>{det}{log.message}</td>
                          <td>{created}</td>
                        </tr>
                      );
                    })}
                  </tbody> */}
                    <tbody>
                      {userLogs.map((log, idx) => {
                        // Mapping of colname to display text
                        const fieldMap = {
                          user_name: "User Name is ",
                          user_fullname: "User Full Name ",
                          user_mob: "Personal Mobile Number is ",
                          user_mob2: "Office Mobile is ",
                          user_whatsapp: "WhatsApp Number is ",
                          user_email: "Personal Email is ",
                          office_email: "Office Email is ",
                          user_dob: "Date of Birth is ",
                          user_pan: "User PAN is ",
                          user_aadhar: "User Aadhar is ",
                          user_address: "User Address is ",
                          user_designation: "User Designation is ",
                          user_image: "User Image is ",
                          incentive_per_subscriber: "Incentive Per Subscriber is ",
                          uan_number: "UAN Number is ",
                          user_aadhar_image: "User Aadhar Image is ",
                          user_pan_image: "User PAN Image is ",
                          bank_name: "Bank Name is ",
                          bank_ifsc: "Bank IFSC is ",
                          bank_account: "Bank Account is ",
                          name_in_bank: "Name in Bank is ",
                          user_type: "User Type is ",
                          basic_pay: "Total CTC is ",
                          join_date: "Join Date is ",
                          left_date: "Left Date is ",
                          suspension_date: "Suspension Date is ",
                          salary: "Per Month Salary is ",
                          incentives: "Incentives is ",
                          incentives_type: "Incentives Type is ",
                          monthly_target: "Monthly Target is ",
                          employmentstatus: "Employment Status is ",
                          employmenttype: "Employment Type is ",
                          user_blocked: "User Status is ",
                          user_category: "User Category is ",
                          sex: "Gender is ",
                          marital_status: "Marital Status is ",
                          father_name: "Father Name is ",
                          joining_interview_by: "Joining Interview By is ",
                          left_interview_by: "Left Interview By is ",
                          left_reason: "Left Reason is ",
                          permanent_address: "Permanent Address is ",
                          nominee_name: "Nominee Name is ",
                          nominee_dob: "Nominee DOB is ",
                          relationship_with_nominee: "Relationship with Nominee is ",
                          mobile_link_adhar: "Linked Adhar Number is ",
                          salary: "Per Month Salary is ",
                          botsense_user: "Botsense User is ",
                          botsense_password: "Botsense Password is ",
                          your_name_in_bank: "Your Name in Bank is ",
                          // Add any additional fields as needed
                        };

                        const det = fieldMap[log.colname] || "";
                        const fullname = log.fullname ? ` - By ${log.fullname} ` : "";

                        const created = log.createdOn
                          ? (() => {
                            const date = new Date(log.createdOn);
                            const day = String(date.getDate()).padStart(2, "0");
                            const month = String(date.getMonth() + 1).padStart(2, "0");
                            const year = date.getFullYear();
                            const hours = String(date.getHours()).padStart(2, "0");
                            const minutes = String(date.getMinutes()).padStart(2, "0");
                            const seconds = String(date.getSeconds()).padStart(2, "0");
                            return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
                          })()
                          : "";

                        return (
                          <tr key={idx}>
                            <td style={{ wordBreak: "break-word" }}>{det}{log.message}{fullname}</td>
                            <td>{created}</td>
                          </tr>
                        );
                      })}
                    </tbody>


                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />{" "}
      {/* Add this line to render the toast notificationss */}
    </div>
  );
};

export default AddUser;
